<template>
  <v-container fluid style="background-color: #f5f5f5; color: #333333; padding: 20px;">
    <h1 class="text-center mb-5 primary--text">Predicciones y Sugerencias de IA</h1>
    
    <!-- Panel de Filtros -->
    <v-card class="mb-6" elevation="3">
      <v-card-title class="primary--text">Filtros de Análisis</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-select
              v-model="filtroRegion"
              :items="regiones"
              label="Región"
              outlined
              dense
              color="primary"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filtroDelito"
              :items="tiposDelito"
              label="Tipo de Delito"
              outlined
              dense
              color="primary"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filtroPeriodo"
              :items="periodos"
              label="Periodo de Análisis"
              outlined
              dense
              color="primary"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3" class="d-flex align-center">
            <v-btn color="primary" block @click="generarPredicciones">
              <v-icon left>mdi-brain</v-icon>
              Generar Predicciones
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    
    <!-- Indicadores Principales -->
    <v-row class="mb-6">
      <v-col cols="12" md="3">
        <v-card elevation="3" class="text-center pa-3">
          <v-icon size="36" color="red darken-1">mdi-alert-circle</v-icon>
          <h2 class="headline mt-2">{{ indicadores.riesgoAlto }}</h2>
          <div class="subtitle-1">Zonas de Alto Riesgo</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card elevation="3" class="text-center pa-3">
          <v-icon size="36" color="orange darken-2">mdi-clock-alert</v-icon>
          <h2 class="headline mt-2">{{ indicadores.horariosPeligrosos }}</h2>
          <div class="subtitle-1">Horarios Críticos</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card elevation="3" class="text-center pa-3">
          <v-icon size="36" color="blue darken-1">mdi-police-badge</v-icon>
          <h2 class="headline mt-2">{{ indicadores.efectivosRecomendados }}</h2>
          <div class="subtitle-1">Efectivos Recomendados</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card elevation="3" class="text-center pa-3">
          <v-icon size="36" color="green darken-1">mdi-percent</v-icon>
          <h2 class="headline mt-2">{{ indicadores.efectividadPrediccion }}%</h2>
          <div class="subtitle-1">Efectividad de Predicción</div>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Mapa de Predicción -->
    <v-row class="mb-6">
      <v-col cols="12" md="8">
        <v-card elevation="3">
          <v-card-title class="primary--text">
            Mapa de Predicción Delictual
            <v-spacer></v-spacer>
            <v-chip color="red" text-color="white" small>Alto</v-chip>
            <v-chip color="orange" text-color="white" small class="mx-1">Medio</v-chip>
            <v-chip color="green" text-color="white" small>Bajo</v-chip>
          </v-card-title>
          <v-card-text>
            <div id="prediction-map" style="height: 400px;"></div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card elevation="3" class="mb-4">
          <v-card-title class="primary--text">Predicción Temporal</v-card-title>
          <v-card-text>
            <apexchart type="heatmap" height="200" :options="heatmapOptions" :series="heatmapSeries"></apexchart>
          </v-card-text>
        </v-card>
        <v-card elevation="3">
          <v-card-title class="primary--text">Tendencia Delictual</v-card-title>
          <v-card-text>
            <apexchart type="line" height="150" :options="trendOptions" :series="trendSeries"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Recomendaciones de IA -->
    <v-card elevation="3" class="mb-6">
      <v-card-title class="primary--text">
        <v-icon left>mdi-lightbulb-on</v-icon>
        Recomendaciones de IA
      </v-card-title>
      <v-card-text>
        <v-timeline dense>
          <v-timeline-item v-for="(recomendacion, index) in recomendaciones" :key="index" small :color="recomendacion.color">
            <div class="font-weight-bold">{{ recomendacion.titulo }}</div>
            <div>{{ recomendacion.descripcion }}</div>
            <div class="caption text-right">Confianza: {{ recomendacion.confianza }}%</div>
          </v-timeline-item>
        </v-timeline>
      </v-card-text>
    </v-card>
    
    <!-- Patrones Identificados -->
    <v-row>
      <v-col cols="12" md="6">
        <v-card elevation="3">
          <v-card-title class="primary--text">Patrones Identificados</v-card-title>
          <v-card-text>
            <v-list two-line>
              <v-list-item v-for="(patron, index) in patrones" :key="index">
                <v-list-item-avatar>
                  <v-icon :color="patron.color">{{ patron.icon }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ patron.titulo }}</v-list-item-title>
                  <v-list-item-subtitle>{{ patron.descripcion }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip small :color="patron.color" text-color="white">{{ patron.relevancia }}%</v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card elevation="3">
          <v-card-title class="primary--text">Asignación Óptima de Recursos</v-card-title>
          <v-card-text>
            <apexchart type="bar" height="300" :options="resourceOptions" :series="resourceSeries"></apexchart>
          </v-card-text>
          <v-card-actions>
            <v-btn text color="primary" @click="exportarPlanificacion">
              <v-icon left>mdi-file-export</v-icon>
              Exportar Planificación
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="aplicarRecomendaciones">
              <v-icon left>mdi-check-all</v-icon>
              Aplicar Recomendaciones
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export default {
  name: 'PrediccionesIAView',
  components: {
    apexchart: VueApexCharts
  },
  data() {
    return {
      // Filtros
      filtroRegion: 'San Juan Capital',
      filtroDelito: 'Todos',
      filtroPeriodo: 'Próximos 7 días',
      
      // Opciones de filtros
      regiones: ['San Juan Capital', 'Rawson', 'Chimbas', 'Rivadavia', 'Santa Lucía', 'Pocito', 'Todas'],
      tiposDelito: ['Todos', 'Hurto', 'Robo', 'Robo agravado', 'Estafa', 'Lesiones', 'Amenazas', 'Daño'],
      periodos: ['Próximas 24 horas', 'Próximos 7 días', 'Próximos 30 días', 'Próximos 90 días'],
      
      // Indicadores
      indicadores: {
        riesgoAlto: 5,
        horariosPeligrosos: 3,
        efectivosRecomendados: 42,
        efectividadPrediccion: 87
      },
      
      // Recomendaciones
      recomendaciones: [
        {
          titulo: 'Aumentar patrullaje en Capital Este',
          descripcion: 'Se recomienda incrementar el patrullaje en la zona este de la capital entre las 20:00 y 23:00 horas.',
          confianza: 92,
          color: 'red'
        },
        {
          titulo: 'Controles vehiculares',
          descripcion: 'Implementar controles vehiculares en accesos a Chimbas durante fines de semana.',
          confianza: 85,
          color: 'orange'
        },
        {
          titulo: 'Campañas de prevención',
          descripcion: 'Realizar campañas de prevención de estafas en zonas comerciales del centro.',
          confianza: 78,
          color: 'blue'
        },
        {
          titulo: 'Vigilancia en transporte público',
          descripcion: 'Aumentar presencia policial en líneas de colectivo con mayor incidencia de hurtos.',
          confianza: 81,
          color: 'green'
        }
      ],
      
      // Patrones
      patrones: [
        {
          titulo: 'Hurtos en transporte público',
          descripcion: 'Aumento de hurtos en líneas de colectivo en horarios pico',
          relevancia: 87,
          icon: 'mdi-bus',
          color: 'deep-purple'
        },
        {
          titulo: 'Robos de motocicletas',
          descripcion: 'Patrón de robo de motocicletas en estacionamientos del centro',
          relevancia: 92,
          icon: 'mdi-motorbike',
          color: 'red'
        },
        {
          titulo: 'Estafas a adultos mayores',
          descripcion: 'Incremento de estafas telefónicas dirigidas a adultos mayores',
          relevancia: 84,
          icon: 'mdi-phone',
          color: 'amber darken-3'
        },
        {
          titulo: 'Robo en viviendas',
          descripcion: 'Patrón de robo en viviendas durante horario laboral en Rivadavia',
          relevancia: 89,
          icon: 'mdi-home',
          color: 'indigo'
        }
      ],
      
      // Opciones para gráficos
      heatmapOptions: {
        chart: {
          type: 'heatmap',
          toolbar: {
            show: false
          },
          foreColor: '#333333'
        },
        dataLabels: {
          enabled: false
        },
        colors: ["#1976D2"],
        title: {
          text: 'Probabilidad de delitos por hora/día',
          align: 'left',
          style: {
            fontSize: '14px'
          }
        },
        xaxis: {
          categories: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
        }
      },
      heatmapSeries: [
        {
          name: "00:00-08:00",
          data: [30, 25, 20, 35, 40, 55, 45]
        },
        {
          name: "08:00-16:00",
          data: [45, 50, 55, 60, 65, 70, 60]
        },
        {
          name: "16:00-24:00",
          data: [75, 70, 65, 80, 90, 95, 85]
        }
      ],
      
      trendOptions: {
        chart: {
          type: 'line',
          toolbar: {
            show: false
          },
          foreColor: '#333333'
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        colors: ['#1976D2'],
        markers: {
          size: 4
        },
        xaxis: {
          categories: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
        },
        title: {
          text: 'Proyección anual',
          align: 'left',
          style: {
            fontSize: '14px'
          }
        }
      },
      trendSeries: [{
        name: 'Casos proyectados',
        data: [30, 40, 45, 50, 49, 60, 70, 91, 125, 105, 80, 60]
      }],
      
      resourceOptions: {
        chart: {
          type: 'bar',
          toolbar: {
            show: false
          },
          foreColor: '#333333'
        },
        plotOptions: {
          bar: {
            horizontal: true,
            dataLabels: {
              position: 'top',
            },
          }
        },
        colors: ['#1976D2'],
        dataLabels: {
          enabled: true,
          offsetX: -6,
          style: {
            fontSize: '12px',
            colors: ['#333333']
          }
        },
        xaxis: {
          categories: ['Capital Este', 'Capital Centro', 'Chimbas', 'Rawson', 'Rivadavia', 'Santa Lucía']
        },
        title: {
          text: 'Distribución óptima de efectivos',
          align: 'left',
          style: {
            fontSize: '14px'
          }
        }
      },
      resourceSeries: [{
        name: 'Efectivos',
        data: [12, 10, 8, 6, 4, 2]
      }]
    };
  },
  mounted() {
    this.initMap();
  },
  methods: {
    initMap() {
      setTimeout(() => {
        try {
          // Coordenadas de San Juan, Argentina
          const sanJuanCoords = [-31.5375, -68.5364];
          const map = L.map('prediction-map').setView(sanJuanCoords, 13);
          
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          }).addTo(map);
          
          // Zonas de predicción con diferentes niveles de riesgo
          const zonas = [
            { center: [-31.5375, -68.5364], name: 'Centro', risk: 'high', radius: 500 },
            { center: [-31.5280, -68.5420], name: 'Capital Norte', risk: 'medium', radius: 400 },
            { center: [-31.5480, -68.5280], name: 'Capital Este', risk: 'high', radius: 450 },
            { center: [-31.5320, -68.5520], name: 'Capital Oeste', risk: 'low', radius: 350 },
            { center: [-31.5580, -68.5350], name: 'Capital Sur', risk: 'medium', radius: 400 },
            { center: [-31.5150, -68.5250], name: 'Chimbas', risk: 'high', radius: 420 },
            { center: [-31.5650, -68.5150], name: 'Rawson', risk: 'medium', radius: 380 },
            { center: [-31.5200, -68.5650], name: 'Rivadavia', risk: 'low', radius: 300 }
          ];
          
          // Añadir círculos para cada zona con color según nivel de riesgo
          zonas.forEach(zona => {
            let color;
            let fillOpacity;
            
            switch(zona.risk) {
              case 'high':
                color = '#f44336'; // Rojo
                fillOpacity = 0.6;
                break;
              case 'medium':
                color = '#ff9800'; // Naranja
                fillOpacity = 0.5;
                break;
              case 'low':
                color = '#4caf50'; // Verde
                fillOpacity = 0.4;
                break;
              default:
                color = '#2196f3'; // Azul
                fillOpacity = 0.3;
            }
            
            L.circle(zona.center, {
              color: color,
              fillColor: color,
              fillOpacity: fillOpacity,
              radius: zona.radius
            }).addTo(map);
            
            // Añadir etiqueta para la zona
            L.marker(zona.center, {
              icon: L.divIcon({
                className: 'prediction-label',
                html: `<div class="zone-name">${zona.name}</div>`,
                iconSize: [100, 20],
                iconAnchor: [50, 10]
              })
            }).addTo(map);
          });
          
          // Intentamos cargar leaflet.heat dinámicamente
          if (window.L && !L.heatLayer) {
            // Si no está disponible, usamos marcadores normales en lugar del mapa de calor
            zonas.forEach(zona => {
              // Determinar la intensidad según el nivel de riesgo
              const numPoints = zona.risk === 'high' ? 15 : (zona.risk === 'medium' ? 10 : 5);
              
              for (let i = 0; i < numPoints; i++) {
                // Generar punto aleatorio dentro de un radio de la zona
                const angle = Math.random() * Math.PI * 2;
                const distance = Math.random() * zona.radius;
                const lat = zona.center[0] + (Math.sin(angle) * distance / 111300); // 111300 metros es aproximadamente 1 grado de latitud
                const lng = zona.center[1] + (Math.cos(angle) * distance / (111300 * Math.cos(zona.center[0] * (Math.PI / 180))));
                
                let markerColor;
                switch(zona.risk) {
                  case 'high': markerColor = '#f44336'; break;
                  case 'medium': markerColor = '#ff9800'; break;
                  case 'low': markerColor = '#4caf50'; break;
                  default: markerColor = '#2196f3';
                }
                
                L.circleMarker([lat, lng], {
                  radius: 4,
                  fillColor: markerColor,
                  color: '#fff',
                  weight: 1,
                  opacity: 0.7,
                  fillOpacity: 0.6
                }).addTo(map);
              }
            });
          }
        } catch (error) {
          console.error('Error al inicializar el mapa:', error);
        }
      }, 500);
    },
    
    generarPredicciones() {
      // Simulación de generación de predicciones
      this.$nextTick(() => {
        // Mostrar notificación de éxito
        this.$emit('show-notification', {
          message: 'Predicciones generadas con éxito',
          color: 'success'
        });
      });
    },
    
    exportarPlanificacion() {
      // Simulación de exportación
      this.$nextTick(() => {
        // Mostrar notificación de éxito
        this.$emit('show-notification', {
          message: 'Planificación exportada correctamente',
          color: 'info'
        });
      });
    },
    
    aplicarRecomendaciones() {
      // Simulación de aplicación de recomendaciones
      this.$nextTick(() => {
        // Mostrar notificación de éxito
        this.$emit('show-notification', {
          message: 'Recomendaciones aplicadas al sistema',
          color: 'success'
        });
      });
    }
  }
};
</script>

<style scoped>
#prediction-map {
  height: 400px;
  z-index: 0;
  border-radius: 4px;
}

.zone-name {
  background-color: rgba(255, 255, 255, 0.7);
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: bold;
  color: #333;
  text-align: center;
  white-space: nowrap;
}
</style>
